import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PRODUCT_FORMATS, SIGNAL_TYPES } from '@commerce/shared';
import { api, ApiClientError } from '../../lib/api.js';
import type { ResearchItem } from '../../lib/types.js';

export function ResearchCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: '',
    summary: '',
    signalType: SIGNAL_TYPES[0] as string,
    sourceUrl: '',
    observedAt: new Date().toISOString().slice(0, 10),
    confidence: 0.5,
    productFormats: [] as string[],
    tags: '',
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<ResearchItem>('/research', body),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['research'] });
      navigate(`/research/${res.data.id}`);
    },
    onError: (err) =>
      setError(err instanceof ApiClientError ? err.message : 'Failed to create research'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const tags = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    mutation.mutate({
      title: form.title,
      summary: form.summary,
      signalType: form.signalType,
      sourceUrl: form.sourceUrl,
      observedAt: new Date(form.observedAt).toISOString(),
      confidence: Number(form.confidence),
      productFormats: form.productFormats,
      tags,
    });
  }

  return (
    <section>
      <h1>New research</h1>
      <form className="form" onSubmit={onSubmit}>
        <label htmlFor="title">Title</label>
        <input
          id="title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />

        <label htmlFor="summary">Summary (aggregate signal only)</label>
        <textarea
          id="summary"
          value={form.summary}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
          required
          rows={4}
        />

        <label htmlFor="signalType">Signal type</label>
        <select
          id="signalType"
          value={form.signalType}
          onChange={(e) => setForm({ ...form, signalType: e.target.value })}
        >
          {SIGNAL_TYPES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <label htmlFor="sourceUrl">Source URL (no direct image links)</label>
        <input
          id="sourceUrl"
          type="url"
          value={form.sourceUrl}
          onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })}
          required
        />

        <label htmlFor="observedAt">Observed date</label>
        <input
          id="observedAt"
          type="date"
          value={form.observedAt}
          onChange={(e) => setForm({ ...form, observedAt: e.target.value })}
          required
        />

        <label htmlFor="confidence">Confidence: {Math.round(form.confidence * 100)}%</label>
        <input
          id="confidence"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={form.confidence}
          onChange={(e) => setForm({ ...form, confidence: Number(e.target.value) })}
        />

        <label htmlFor="productFormats">Product formats</label>
        <select
          id="productFormats"
          multiple
          value={form.productFormats}
          onChange={(e) =>
            setForm({
              ...form,
              productFormats: Array.from(e.target.selectedOptions, (o) => o.value),
            })
          }
        >
          {PRODUCT_FORMATS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        <label htmlFor="tags">Tags (comma-separated)</label>
        <input
          id="tags"
          value={form.tags}
          onChange={(e) => setForm({ ...form, tags: e.target.value })}
        />

        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}

        <button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Create draft'}
        </button>
      </form>
    </section>
  );
}
